import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  errorMessage,
  useAccounts,
  useAllWorkspaces,
  useApproveAccount,
  useAuditLog,
  useCapacity,
  useDisableAccount,
  useGrantJeanZay,
  useRevokeJeanZay,
  useSetPaused,
  useStopWorkspace,
  useUsage,
} from "../api/queries";
import type {
  AccountUsage,
  AdminUser,
  AuditEntry,
  Capacity,
  Workspace,
} from "../api/types";
import { JEAN_ZAY_CAPABILITY } from "../api/types";
import { useApi } from "../api/ApiProvider";
import { useSession } from "../auth/SessionProvider";
import { AUDIT_LIMIT } from "../config";
import { PageHeader } from "../components/layout/PageHeader";
import { Dialog } from "../components/ui/Dialog";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";
import { workloadFamilyLabel } from "../lib/capacity";
import {
  formatDateTime,
  formatElapsed,
  formatGpuHours,
  pluralize,
} from "../lib/format";

/** Human label for an account state. */
const STATE_LABEL: Record<AdminUser["state"], string> = {
  pending: "waiting for approval",
  active: "approved",
  disabled: "turned off",
};

/**
 * Human label for what has been recorded about an account.
 *
 * One exists today, and it is a record rather than a permission: the badge says
 * what the team wrote down, not what the person may do. An unknown capability
 * is still shown, spelled as the server spells it — something this build has
 * never heard of is exactly what an operator must be able to see.
 */
const CAPABILITY_LABEL: Record<string, string> = {
  [JEAN_ZAY_CAPABILITY]: "Declared to IDRIS",
};

interface StopButtonProps {
  workspace: Workspace;
}

/**
 * Stop somebody else's workspace, after saying plainly what that costs them.
 *
 * Args:
 *     workspace: The workspace to stop.
 *
 * Returns:
 *     The stop button and its confirmation dialog.
 */
function StopWorkspaceButton({ workspace }: StopButtonProps): JSX.Element {
  const [asking, setAsking] = useState(false);
  const stop = useStopWorkspace(workspace.id);

  return (
    <>
      <button
        type="button"
        className="btn btn--xs btn--danger"
        onClick={() => setAsking(true)}
      >
        Stop
      </button>
      {asking ? (
        <Dialog
          title={`Stop ${workspace.user}'s workspace?`}
          onClose={() => setAsking(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => setAsking(false)}
              >
                Keep it running
              </button>
              <button
                type="button"
                className="btn btn--sm btn--danger"
                disabled={stop.isPending}
                onClick={() =>
                  stop.mutate(undefined, { onSuccess: () => setAsking(false) })
                }
              >
                {stop.isPending ? "Stopping…" : "Stop it"}
              </button>
            </>
          }
        >
          <p className="dialog__text">
            {workspace.user} loses their session immediately, and anything
            running in it stops. Their home folder is kept; the fast local disk
            of that machine is wiped.
          </p>
          <p className="dialog__text">
            They hold {pluralize(workspace.gpus, "GPU")}, which go back to the
            pool.
          </p>
          {stop.isError ? (
            <p className="dialog__error" role="alert">
              {errorMessage(stop.error)}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </>
  );
}

interface DisableButtonProps {
  user: AdminUser;
}

/** Turn an account off, with the reason the audit log will carry. */
function DisableAccountButton({ user }: DisableButtonProps): JSX.Element {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const disable = useDisableAccount();

  return (
    <>
      <button
        type="button"
        className="btn btn--xs"
        onClick={() => setAsking(true)}
      >
        Disable
      </button>
      {asking ? (
        <Dialog
          title={`Turn off ${user.username}?`}
          onClose={() => setAsking(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => setAsking(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--sm btn--danger"
                disabled={disable.isPending || reason.trim().length === 0}
                onClick={() =>
                  disable.mutate(
                    { username: user.username, reason: reason.trim() },
                    { onSuccess: () => setAsking(false) },
                  )
                }
              >
                {disable.isPending ? "Turning off…" : "Turn it off"}
              </button>
            </>
          }
        >
          <p className="dialog__text">
            They can no longer open or reach a workspace, and the one they have
            running is stopped. Nothing is deleted: the files and the history
            stay, and the account can be approved again.
          </p>
          <label className="field__label" htmlFor="disable-reason">
            Reason (recorded in the audit log)
          </label>
          <input
            id="disable-reason"
            className="input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Left the team"
          />
          {disable.isError ? (
            <p className="dialog__error" role="alert">
              {errorMessage(disable.error)}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </>
  );
}

/**
 * Write down that the team has declared an account to IDRIS, or take it back.
 *
 * The one thing this toggle must not do is read like a lock. It does **not**
 * give anybody a shell on the machine of *Training elsewhere*: every approved
 * researcher with a key is already on that machine's list, because reaching
 * our own MLflow from wherever a run happens comes with approval
 * (`docs/AUTH.md` §2, ADR-0011). What it changes is a record — who we told
 * IDRIS about — and, as a consequence of that record, whether the researcher
 * is shown the Jean Zay half of the instructions or only the MLflow half.
 *
 * Both acts are idempotent on the server and audited, and neither takes a
 * reason, so the dialog asks for nothing: it only says what will and will not
 * happen.
 *
 * Args:
 *     user: The account this row is about.
 *
 * Returns:
 *     The toggle and its confirmation dialog.
 */
function JeanZayButton({ user }: { user: AdminUser }): JSX.Element {
  const [asking, setAsking] = useState(false);
  const declared = user.entitlements.includes(JEAN_ZAY_CAPABILITY);
  const grant = useGrantJeanZay();
  const revoke = useRevokeJeanZay();
  const mutation = declared ? revoke : grant;

  return (
    <>
      <button
        type="button"
        className="btn btn--xs"
        onClick={() => setAsking(true)}
      >
        {declared ? "Remove IDRIS record" : "Record IDRIS declaration"}
      </button>
      {asking ? (
        <Dialog
          title={
            declared
              ? `Take back the IDRIS record for ${user.username}?`
              : `Record that ${user.username} is declared to IDRIS?`
          }
          onClose={() => setAsking(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => setAsking(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn btn--sm ${declared ? "btn--danger" : "btn--primary"}`}
                disabled={mutation.isPending}
                onClick={() =>
                  mutation.mutate(user.username, {
                    onSuccess: () => setAsking(false),
                  })
                }
              >
                {mutation.isPending
                  ? declared
                    ? "Taking it back…"
                    : "Recording…"
                  : declared
                    ? "Take it back"
                    : "Record it"}
              </button>
            </>
          }
        >
          {declared ? (
            <>
              <p className="dialog__text">
                The Jean Zay instructions stop being shown to them: their
                <em> Training elsewhere</em> screen keeps the MLflow tunnel and
                loses that half. It does <strong>not</strong> take away their
                access to that machine — being approved is what gives them
                that, and they still are.
              </p>
              <p className="dialog__text">
                It does not close anything at the national centre either: their
                IDRIS account is theirs, and this console neither issued it nor
                can revoke it. If they must lose it, IDRIS is who to tell.
              </p>
              <p className="dialog__text">
                Nothing here is deleted: the record that we had declared them,
                and when, is kept. It can be written again.
              </p>
            </>
          ) : (
            <>
              <p className="dialog__text">
                This writes down that the team has declared{" "}
                <span className="mono">{user.username}</span> to IDRIS, and
                shows them the Jean Zay half of{" "}
                <em>Training elsewhere</em> — how to reach the national
                supercomputer through our machine — instead of only the MLflow
                tunnel.
              </p>
              <p className="dialog__text">
                It gives them nothing they did not have. They can already log
                into that machine: every approved account with an SSH key is on
                its list, and this changes no list. Getting onto Jean Zay itself
                still needs their own personal IDRIS account, which this console
                does not create and does not hold.
              </p>
              {user.state === "active" ? null : (
                <p className="dialog__text">
                  {user.state === "disabled"
                    ? "This account is disabled, so it reaches nothing at all until somebody approves it again. The record can be written now regardless."
                    : "This account is not approved yet, so it reaches nothing at all until somebody approves it. The record can be written now regardless."}
                </p>
              )}
            </>
          )}
          {mutation.isError ? (
            <p className="dialog__error" role="alert">
              {errorMessage(mutation.error)}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </>
  );
}

/** The emergency stop, and the reason it was pulled. */
function PauseSwitch({ paused }: { paused: boolean }): JSX.Element {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const setPaused = useSetPaused();

  if (paused) {
    return (
      <div className="admin__switch">
        <p className="admin__switch-text">
          The console is paused: no new workspace and no new machine. Work
          already running was not touched.
        </p>
        <button
          type="button"
          className="btn btn--sm"
          disabled={setPaused.isPending}
          onClick={() => setPaused.mutate({ paused: false, reason: "" })}
        >
          {setPaused.isPending ? "Resuming…" : "Resume the console"}
        </button>
        {setPaused.isError ? (
          <p className="form__error" role="alert">
            {errorMessage(setPaused.error)}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="admin__switch">
      <p className="admin__switch-text">
        Pausing refuses every new workspace and every new machine, cluster-wide.
        It never stops work that is already running — killing somebody's
        training to fix a console problem is its own incident.
      </p>
      <button
        type="button"
        className="btn btn--sm btn--warning"
        onClick={() => setAsking(true)}
      >
        Pause the console
      </button>
      {asking ? (
        <Dialog
          title="Pause the console?"
          onClose={() => setAsking(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => setAsking(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--sm btn--warning"
                disabled={setPaused.isPending || reason.trim().length === 0}
                onClick={() =>
                  setPaused.mutate(
                    { paused: true, reason: reason.trim() },
                    { onSuccess: () => setAsking(false) },
                  )
                }
              >
                {setPaused.isPending ? "Pausing…" : "Pause it"}
              </button>
            </>
          }
        >
          <p className="dialog__text">
            Everybody sees a banner saying the console is paused. Running
            workspaces keep running, and keep their deadline.
          </p>
          <label className="field__label" htmlFor="pause-reason">
            Reason (recorded in the audit log, shown to nobody)
          </label>
          <input
            id="pause-reason"
            className="input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Budget review"
          />
          {setPaused.isError ? (
            <p className="dialog__error" role="alert">
              {errorMessage(setPaused.error)}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </div>
  );
}

/** Accounts waiting for somebody to say yes. The action of launch day. */
function PendingAccounts({ users }: { users: AdminUser[] }): JSX.Element {
  const approve = useApproveAccount();

  if (users.length === 0) {
    return <EmptyBlock label="Nobody is waiting for approval." />;
  }

  return (
    <>
      <table className="admin-table">
        <thead>
          <tr>
            <th scope="col">Account</th>
            <th scope="col">Signed in first</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.username}>
              <td>
                <span className="admin-table__name">{user.username}</span>
                {user.email ? (
                  <span className="admin-table__sub mono">{user.email}</span>
                ) : null}
              </td>
              <td className="admin-table__muted">
                {formatDateTime(user.first_seen_at)}
              </td>
              <td className="admin-table__actions">
                <button
                  type="button"
                  className="btn btn--xs btn--primary"
                  disabled={approve.isPending}
                  onClick={() => approve.mutate(user.username)}
                >
                  Approve
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {approve.isError ? (
        <p className="form__error" role="alert">
          {errorMessage(approve.error)}
        </p>
      ) : null}
    </>
  );
}

/** Every account, whatever its state. */
function AllAccounts({ users }: { users: AdminUser[] }): JSX.Element {
  if (users.length === 0) {
    return <EmptyBlock label="No account has ever signed in." />;
  }

  return (
    <table className="admin-table">
      <thead>
        <tr>
          <th scope="col">Account</th>
          <th scope="col">State</th>
          <th scope="col">Role and records</th>
          <th scope="col">GPU-hours this week</th>
          <th scope="col">Workspace</th>
          <th scope="col" />
        </tr>
      </thead>
      <tbody>
        {users.map((user) => (
          <tr key={user.username}>
            <td>
              <span className="admin-table__name">{user.username}</span>
              {user.email ? (
                <span className="admin-table__sub mono">{user.email}</span>
              ) : null}
            </td>
            <td>
              <span className="badge" data-state={user.state}>
                {STATE_LABEL[user.state] ?? user.state}
              </span>
              {user.disabled_reason ? (
                <span className="admin-table__sub">{user.disabled_reason}</span>
              ) : null}
            </td>
            <td className="admin-table__badges">
              {/*
                One permission and one fact, side by side because they are read
                together and confused otherwise: `admin` decides for other
                people; an entitlement decides nothing, it records what the team
                wrote down (`docs/AUTH.md` §2). Neither implies the other.
              */}
              {user.is_admin ? <span className="badge">admin</span> : null}
              {user.entitlements.map((capability) => (
                <span key={capability} className="badge" data-tone="ok">
                  {CAPABILITY_LABEL[capability] ?? capability}
                </span>
              ))}
              {!user.is_admin && user.entitlements.length === 0 ? (
                <span className="admin-table__muted">—</span>
              ) : null}
            </td>
            <td className="admin-table__muted">
              {formatGpuHours(user.weekly_gpu_hours_used)}
            </td>
            <td className="admin-table__muted">
              {user.workspace_id ? "one running" : "—"}
            </td>
            <td className="admin-table__actions">
              <JeanZayButton user={user} />
              {user.state === "disabled" ? null : (
                <DisableAccountButton user={user} />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The periods the usage report can be read over, in days. */
const USAGE_PERIODS = [7, 30, 90] as const;
type UsagePeriod = (typeof USAGE_PERIODS)[number];

/**
 * Format hours to one decimal, for the usage table.
 *
 * Args:
 *     hours: The figure, or `null` when it was never measured.
 *
 * Returns:
 *     The number, or a dash for "never measured".
 */
function formatHours(hours: number | null): string {
  return hours === null ? "—" : formatGpuHours(hours);
}

/**
 * The busy share of a person's GPU-hours, as a percentage.
 *
 * Args:
 *     row: One person's figures, or the totals.
 *
 * Returns:
 *     `"64 %"`, or a dash when nothing was held or nothing was measured.
 */
function busyShare(row: AccountUsage): string {
  if (row.busy_gpu_hours === null || row.gpu_hours <= 0) {
    return "—";
  }
  return `${Math.round((100 * row.busy_gpu_hours) / row.gpu_hours)} %`;
}

/**
 * One row of the usage table — a person, or the totals.
 *
 * Args:
 *     row: The figures.
 *     label: What the first cell says; the username by default.
 *
 * Returns:
 *     The table row.
 */
function UsageRow({
  row,
  label,
}: {
  row: AccountUsage;
  label?: string;
}): JSX.Element {
  return (
    <tr>
      <td>
        <span className="admin-table__name">{label ?? row.username}</span>
        {row.email ? (
          <span className="admin-table__sub mono">{row.email}</span>
        ) : null}
      </td>
      <td className="admin-table__muted">{formatHours(row.gpu_hours)}</td>
      <td className="admin-table__muted">
        {formatHours(row.busy_gpu_hours)}
        {row.busy_gpu_hours === null ? null : (
          <span className="admin-table__sub">{busyShare(row)}</span>
        )}
      </td>
      <td className="admin-table__muted">{formatHours(row.cpu_core_hours)}</td>
      <td className="admin-table__muted">{row.workspaces}</td>
      <td className="admin-table__muted">{row.runs}</td>
    </tr>
  );
}

/**
 * What every person consumed over a period: the place the bill gets names.
 *
 * Information, never a quota (ADR-0008): the sentence under the heading says
 * so, because a table of hours per person reads as a budget until it is told
 * not to be one.
 *
 * Returns:
 *     The period selector and the table.
 */
function UsageSection(): JSX.Element {
  const [days, setDays] = useState<UsagePeriod>(30);
  const usage = useUsage(days);

  return (
    <section className="section" aria-labelledby="admin-usage">
      <div className="section__head">
        <h2 className="section__title" id="admin-usage">
          Usage per person
        </h2>
        <span className="section__meta">
          {USAGE_PERIODS.map((period) => (
            <button
              key={period}
              type="button"
              className="btn btn--xs"
              aria-pressed={period === days}
              onClick={() => setDays(period)}
            >
              {period} days
            </button>
          ))}
        </span>
      </div>
      <p className="section__lead">
        GPU-hours are what each person held, from the console&apos;s own
        record of every stretch a workspace or job sat on a machine. &ldquo;Of
        which busy&rdquo; is how much of that the GPUs were measured computing,
        read from the GPU telemetry on every pass since the console started
        counting — an approximation, and a dash means it was never measured.
        This is information for whoever pays the bill, not a quota: nothing
        here refuses anybody anything.
      </p>
      {usage.isPending ? (
        <LoadingBlock label="Adding up the hours…" />
      ) : usage.isError ? (
        <ErrorBlock error={usage.error} onRetry={() => void usage.refetch()} />
      ) : usage.data.users.length === 0 ? (
        <EmptyBlock label={`Nobody held a GPU or a core in the last ${days} days.`} />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Account</th>
              <th scope="col">GPU-hours</th>
              <th scope="col">Of which busy</th>
              <th scope="col">CPU core-hours</th>
              <th scope="col">Workspaces</th>
              <th scope="col">Runs</th>
            </tr>
          </thead>
          <tbody>
            {usage.data.users.map((row) => (
              <UsageRow key={row.username} row={row} />
            ))}
          </tbody>
          <tfoot>
            <UsageRow row={usage.data.totals} label="Everybody" />
          </tfoot>
        </table>
      )}
    </section>
  );
}

/** Every live workspace: who, since when, how many GPUs, and a way to stop it. */
function LiveWorkspaces({
  workspaces,
  capacity,
}: {
  workspaces: Workspace[];
  capacity: Capacity | undefined;
}): JSX.Element {
  if (workspaces.length === 0) {
    return <EmptyBlock label="No workspace is running." />;
  }

  return (
    <table className="admin-table">
      <thead>
        <tr>
          <th scope="col">Who</th>
          <th scope="col">GPUs</th>
          <th scope="col">Running for</th>
          <th scope="col">State</th>
          <th scope="col" />
        </tr>
      </thead>
      <tbody>
        {workspaces.map((workspace) => (
          <tr key={workspace.id}>
            <td>
              <span className="admin-table__name">{workspace.user}</span>
              <Link
                className="admin-table__sub mono"
                to={`/workspaces/${workspace.id}`}
              >
                {workspace.id.slice(0, 8)}
              </Link>
            </td>
            {/* On a fleet with more than one kind of GPU a count alone does not
                say what is held: 8 L4s and 8 H100s are the same number and a
                very different machine. */}
            <td className="admin-table__muted">
              {[workspace.gpus, workloadFamilyLabel(capacity, workspace.gpu_family)]
                .filter((part) => part !== null)
                .join(" ")}
            </td>
            <td className="admin-table__muted">
              {formatElapsed(workspace.started_at ?? workspace.created_at)}
            </td>
            <td className="admin-table__muted">
              {workspace.state.replace(/_/g, " ")}
            </td>
            <td className="admin-table__actions">
              <StopWorkspaceButton workspace={workspace} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** How long a search box may go quiet before it is sent. */
const AUDIT_SEARCH_DEBOUNCE_MS = 300;

/** The two things an admin narrows the log by. */
interface AuditFilterState {
  q?: string;
  actor?: string;
}

/** Turn what was typed into filters: blanks are "no filter". */
function auditFilters(q: string, actor: string): AuditFilterState {
  return { q: q.trim() || undefined, actor: actor.trim() || undefined };
}

function sameFilters(a: AuditFilterState, b: AuditFilterState): boolean {
  return a.q === b.q && a.actor === b.actor;
}

/**
 * Search, page and download the audit log.
 *
 * Fifty rows a page, the total behind them, a search box that sends itself
 * after a short pause or on Enter, and a download of every matching row —
 * the log is what somebody reads after an incident, and an incident is a
 * question, not a scroll.
 *
 * Returns:
 *     The section.
 */
function AuditSection(): JSX.Element {
  const api = useApi();
  const [typed, setTyped] = useState("");
  const [actorTyped, setActorTyped] = useState("");
  const [filters, setFilters] = useState<AuditFilterState>({});
  const [offset, setOffset] = useState(0);

  // A new search starts on its first page — in the same update as the
  // filters, so no request ever goes out for page two of a search that
  // has just begun.
  const apply = (): void => {
    const next = auditFilters(typed, actorTyped);
    if (!sameFilters(filters, next)) {
      setFilters(next);
      setOffset(0);
    }
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      const next = auditFilters(typed, actorTyped);
      if (!sameFilters(filters, next)) {
        setFilters(next);
        setOffset(0);
      }
    }, AUDIT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [typed, actorTyped, filters]);

  const audit = useAuditLog({ limit: AUDIT_LIMIT, offset, ...filters });
  const filtered = filters.q !== undefined || filters.actor !== undefined;

  return (
    <section className="section" aria-labelledby="admin-audit">
      <div className="section__head">
        <h2 className="section__title" id="admin-audit">
          Audit log
        </h2>
        <span className="section__meta">
          {audit.data
            ? audit.data.total === 0
              ? "nothing matches"
              : `showing ${(audit.data.offset + 1).toLocaleString("en-GB")}–${Math.min(
                  audit.data.offset + audit.data.limit,
                  audit.data.total,
                ).toLocaleString("en-GB")} of ${audit.data.total.toLocaleString("en-GB")}, newest first`
            : "newest first"}
        </span>
      </div>
      <div className="audit-toolbar">
        <input
          className="input"
          type="search"
          aria-label="Search the audit log"
          placeholder="Search who, what, to whom, why…"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              apply();
            }
          }}
        />
        <input
          className="input input--mono audit-toolbar__actor"
          type="text"
          aria-label="Only this account"
          placeholder="account, or console"
          value={actorTyped}
          onChange={(event) => setActorTyped(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              apply();
            }
          }}
        />
        <a className="btn btn--xs" href={api.auditExportUrl(filters)} download>
          Download CSV
        </a>
      </div>
      <p className="section__lead">
        The download is every row the search matches, newest first, not just
        this page.
      </p>
      {audit.isPending ? (
        <LoadingBlock label="Reading the audit log…" />
      ) : audit.isError ? (
        <ErrorBlock error={audit.error} onRetry={() => void audit.refetch()} />
      ) : (
        <>
          <AuditTable entries={audit.data.items} filtered={filtered} />
          {audit.data.total > audit.data.limit ? (
            <div className="audit-pager">
              <button
                type="button"
                className="btn btn--xs"
                disabled={audit.data.offset === 0}
                onClick={() =>
                  setOffset(Math.max(0, audit.data.offset - audit.data.limit))
                }
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn--xs"
                disabled={audit.data.offset + audit.data.limit >= audit.data.total}
                onClick={() => setOffset(audit.data.offset + audit.data.limit)}
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

/** What was done, by whom, to whom, and why. */
function AuditTable({
  entries,
  filtered,
}: {
  entries: AuditEntry[];
  filtered: boolean;
}): JSX.Element {
  if (entries.length === 0) {
    return (
      <EmptyBlock
        label={
          filtered
            ? "Nothing matches — try fewer words, or clear the account."
            : "Nothing has been recorded yet."
        }
      />
    );
  }

  return (
    <table className="admin-table">
      <thead>
        <tr>
          <th scope="col">When</th>
          <th scope="col">Who</th>
          <th scope="col">Did what</th>
          <th scope="col">To</th>
          <th scope="col">Why</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => (
          <tr key={entry.id}>
            <td className="admin-table__muted">{formatDateTime(entry.at)}</td>
            <td className="mono">{entry.actor}</td>
            <td>{entry.action.replace(/_/g, " ")}</td>
            <td className="mono">{entry.subject ?? "—"}</td>
            <td className="admin-table__muted">{entry.reason ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * The operator screen: approve people, see the fleet, stop it if needed.
 *
 * Dense on purpose. It is not a product screen: whoever opens it is looking
 * for one row and one button, usually under time pressure.
 *
 * Returns:
 *     The administration screen, or a plain refusal for a non-admin.
 */
export function AdminPage(): JSX.Element {
  const { me } = useSession();
  const accounts = useAccounts();
  const workspaces = useAllWorkspaces();
  // Read for one reason: to know whether this deployment has more than one
  // kind of GPU, and so whether the GPU column has to name which.
  const capacity = useCapacity();

  const header = (
    <PageHeader
      eyebrow="Operations"
      title="Administration"
      subtitle="Approvals, live work, and the emergency stop."
      back={{ to: "/", label: "GPUs" }}
    />
  );

  if (!me.is_admin) {
    return (
      <>
        {header}
        <section className="section">
          <p className="gate__text">
            This screen is for the people who approve accounts and stop
            machines. Your account is not one of them.
          </p>
        </section>
      </>
    );
  }

  const users = accounts.data?.items ?? [];
  const pending = users.filter((user) => user.state === "pending");

  return (
    <>
      {header}

      <section className="section" aria-labelledby="admin-pending">
        <div className="section__head">
          <h2 className="section__title" id="admin-pending">
            Waiting for approval
          </h2>
          <span className="section__meta">
            {pluralize(pending.length, "account")}
          </span>
        </div>
        {accounts.isPending ? (
          <LoadingBlock label="Reading the accounts…" />
        ) : accounts.isError ? (
          <ErrorBlock
            error={accounts.error}
            onRetry={() => void accounts.refetch()}
          />
        ) : (
          <PendingAccounts users={pending} />
        )}
      </section>

      <section className="section" aria-labelledby="admin-accounts">
        <div className="section__head">
          <h2 className="section__title" id="admin-accounts">
            All accounts
          </h2>
          <span className="section__meta">{pluralize(users.length, "account")}</span>
        </div>
        {accounts.isSuccess ? <AllAccounts users={users} /> : null}
      </section>

      <UsageSection />

      <section className="section" aria-labelledby="admin-workspaces">
        <div className="section__head">
          <h2 className="section__title" id="admin-workspaces">
            Live workspaces
          </h2>
          <span className="section__meta">
            {pluralize(workspaces.data?.items.length ?? 0, "workspace")}
          </span>
        </div>
        {workspaces.isPending ? (
          <LoadingBlock label="Reading the running workspaces…" />
        ) : workspaces.isError ? (
          <ErrorBlock
            error={workspaces.error}
            onRetry={() => void workspaces.refetch()}
          />
        ) : (
          <LiveWorkspaces
            workspaces={workspaces.data.items}
            capacity={capacity.data}
          />
        )}
      </section>

      <section className="section" aria-labelledby="admin-pause">
        <h2 className="section__title" id="admin-pause">
          Emergency stop
        </h2>
        <PauseSwitch paused={me.console_paused} />
      </section>

      <AuditSection />
    </>
  );
}
